// Port of ios/Sources/ViewModels/ConversationLanding.swift.
//
// Where a conversation opens, and when it may go looking for older history.
//
// Both answers used to be implicit in SwiftUI's defaults, and both were wrong:
//
// 1. **A `ScrollView` opens at the TOP.** The timeline is ordered oldest → newest, so a
//    conversation opened at whatever the oldest row in the window happened to be — "27 de agosto"
//    on a photo of an iPad, three weeks above the newest message (reported 2026-09-15, "not
//    scrolling automatically to the bottom"). The Mac has landed at the bottom since it shipped
//    (`MacConversationView.anchorToBottom`) and Android since 1.0.174; the phone never did.
// 2. **The older-history trigger was the top spinner's `onAppear`.** Opening at the top put that
//    spinner on screen immediately, so every open fetched a page it had no reason to want, and
//    kept fetching as each page arrived — a spinner that never stops, which is what "not loading
//    prev messages" looks like from the outside. The Mac hit the same fault on 2026-09-08 and
//    fixed it by refusing to ask until the reader has actually arrived.
//
// Kept as plain decisions, with nothing from the view layer in the room, so they are unit tests
// rather than something only a device can answer (the same rule `AdaptiveLayout` follows).

/**
 * Whether the conversation should scroll itself to the newest message.
 *
 * - `restoredAnchor`: the row this conversation was left on, if the reader has been here
 *   before in this session. Returning to where you were beats landing at the end.
 * - `pendingJump`: a permalink/search/notification target the view is about to jump to; the
 *   jump owns the scroll and must not be fought for it.
 * - `hasRows`: nothing to land ON until the first rows exist.
 * - `alreadyLanded`: this is a one-shot for the OPEN, not a rule that re-centres the reader
 *   every time a message arrives.
 *
 * There is deliberately no "is the clearance ready?" condition: the compose bar is a
 * `safeAreaInset` on the timeline, so the scroll view already knows how much room it takes and
 * `scrollTo(_:anchor: .bottom)` lands above it. An earlier attempt reserved that room by hand
 * and had to wait for a measurement — which is how the last messages ended up under the bar
 * twice ("some messages are below the compose box").
 */
function shouldLandAtNewest(args: {
  restoredAnchor: string | null;
  pendingJump: string | null;
  hasRows: boolean;
  alreadyLanded: boolean;
}): boolean {
  if (!args.hasRows || args.alreadyLanded) return false;
  if (args.restoredAnchor != null || args.pendingJump != null) return false;
  return true;
}

/**
 * Whether reaching the top of the built list may ask for another page of history.
 *
 * The gate is "has this conversation finished opening" — before that, the top of the list is
 * on screen because the list has not been placed yet, not because the reader scrolled there.
 *
 * A page that FAILED stops the automatic asking until the reader says otherwise. Without that,
 * a trigger driven by geometry re-asks on the very next layout pass, for as long as the top of
 * the list is on screen: a retry storm that pinned the app's main thread for thirty seconds in
 * the capture harness. The failure has a Retry of its own (`TopState`), and that is the hand
 * that should restart it.
 */
function mayLoadOlder(args: {
  hasLanded: boolean;
  isLoadingOlder: boolean;
  hasMoreOlder: boolean;
  lastAttemptFailed: boolean;
}): boolean {
  return args.hasLanded && !args.isLoadingOlder && args.hasMoreOlder && !args.lastAttemptFailed;
}

/**
 * What the top of the list should say for itself, so a reader can tell a page being fetched
 * from a page that failed from "this is the beginning" — three states that all looked like one
 * permanently spinning wheel.
 *
 * - `loading`: a page is in flight.
 * - `failed`: the last attempt failed and can be tried again.
 * - `beginning`: there is no more history: this is where the conversation starts. It used to be
 *   folded into `idle` — nothing drawn — and that was still one of the three situations a reader
 *   could not tell apart. A conversation four messages long looked exactly like one whose history
 *   had not arrived yet (Android, 2026-09-17), so the start of a conversation now says that it is
 *   the start.
 * - `idle`: there is more, and nobody has asked for it yet.
 */
export type ConversationTopState = 'loading' | 'failed' | 'beginning' | 'idle';

function topState(args: { isLoadingOlder: boolean; olderLoadFailed: boolean; hasMoreOlder: boolean }): ConversationTopState {
  if (args.isLoadingOlder) return 'loading';
  if (args.olderLoadFailed && args.hasMoreOlder) return 'failed';
  if (!args.hasMoreOlder) return 'beginning';
  return 'idle';
}

export const ConversationLanding = {
  shouldLandAtNewest,
  mayLoadOlder,
  topState,
} as const;
